import React, { useEffect, useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Dropdown, Spinner } from "react-bootstrap";
import { auth } from "../../../services/firebase";
import { connect } from "react-redux";
import {
  addUserCard,
  clearBillingAddress,
  getUserDetails,
  searchAddresses,
  selectAddress,
} from "../../actions";
import PhoneInput from "react-phone-input-2";
import {
  BILLING,
  CREDIT_CARD,
  MPESA,
  SAFARICOM,
  STRIPE,
} from "../../../constants/types";
import MessageBlock from "../common/MessageBlock";
import AddressSelectItem from "../Address/AddressSelectItem";
import AddressForm from "../Address/AddressForm";
import AddressListModal from "../Address/AddressListModal";
import { FiInfo } from "react-icons/fi";
import PAYSTACK_COUNTRIES from "../../../assets/paystackCountries.json";
import { PaystackButton } from "react-paystack";
import { isValidPhoneNumber } from "../common/helpers";

// Set to true to re-enable Paystack card flow and billing address selector
const ENABLE_PAYSTACK =
  process.env.REACT_APP_ENABLE_PAYSTACK &&
  process.env.REACT_APP_ENABLE_PAYSTACK === "true";

const CardForm = (props) => {
  const stripe = useStripe();
  const elements = useElements();

  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [formType, setFormType] = useState(CREDIT_CARD);
  const [showAddrList, setShowAddrList] = useState(false);
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState("");

  useEffect(() => {
    if (props.country && props.country?.toString().toLowerCase() === "ke") {
      setFormType(MPESA);
    }
    if (
      ENABLE_PAYSTACK &&
      !props.billingAddress?.id &&
      !!props.defaultAddress?.id
    ) {
      props.selectAddress(props.defaultAddress, BILLING);
    }
  }, []);

  useEffect(() => {
    setError("");
  }, [formType, phone]);

  const saveStripeCard = async () => {
    const billingDetails = {
      email: props.info?.email || auth()?.currentUser?.email || "Unknown",
    };
    const element = elements.getElement(CardElement);

    props.addUserCard(
      {
        provider: STRIPE,
        stripe: stripe,
        element: element,
        billingDetails: billingDetails,
        data: {
          payment_method_type: CREDIT_CARD,
          details: {},
        },
      },
      (id) => {
        props.getUserDetails(() => {
          props.onFinish(id);
        });

        if (ENABLE_PAYSTACK) {
          props.selectAddress(
            props.defaultAddress || props.billingAddress,
            BILLING,
          );
        }
      },
    );
  };

  const saveMPESACard = async () => {
    //remove prefixed '0' if any and add '+' since the PhoneInput component leaves it out
    let validatedPhone = phone;
    if (validatedPhone.startsWith(`2540`)) {
      validatedPhone = validatedPhone.replace(`2540`, "254");
    }

    if (!isValidPhoneNumber(validatedPhone)) {
      setError("Please enter a valid number");
      return;
    }

    let formattedPhone = "+" + validatedPhone;

    props.addUserCard(
      {
        payment_method_type: MPESA,
        details: { mobile_money_phone_number: formattedPhone },
        provider: SAFARICOM,
      },
      (id) => {
        props.getUserDetails(() => {
          props.onFinish(id);
        });

        if (ENABLE_PAYSTACK) {
          props.selectAddress(
            props.defaultAddress || props.billingAddress,
            BILLING,
          );
        }
      },
    );
  };
  const paystackCountry = PAYSTACK_COUNTRIES.find(
    (country) => props.billingAddress.country === country.country,
  );

  const paystackOptions = {
    publicKey:
      process.env.REACT_APP_PAYSTACK_KEY ||
      "pk_test_bacd375871053e1c369dbddcdd53c30b8c7da797",
    email: props.info?.email || "customer@example.com",
    amount:
      parseFloat(
        props.paystackCharge?.amount || paystackCountry?.minAmount || 1,
      ) * parseFloat(paystackCountry?.subUnitMultiplier || 100), // in kobo
    currency:
      props.paystackCharge?.currency || paystackCountry?.currency || "KES",
    text: "Add and Pay with Credit Card",
    onSuccess: (transaction) => {
      if (transaction.status === "success") {
        setCurrentTransaction(transaction.reference);
        props.paystackCharge?.onSuccess(transaction.reference);
      }
    },
    onCancel: () => {
      console.log("Payment canceled");
    },
  };

  const renderCreditCardForm = () => {
    if (ENABLE_PAYSTACK && !props.billingAddress?.id) {
      return (
        <MessageBlock
          type={"warning"}
          icon={<FiInfo />}
          message={"Select a billing address to continue with payment"}
        />
      );
    }

    if (
      ENABLE_PAYSTACK &&
      !!paystackCountry &&
      paystackCountry.country === "KE"
    ) {
      return (
        <div className={"paystack-button"}>
          {props.paystackCharge?.loading ? (
            <button>
              <Spinner animation={"border"} />
            </button>
          ) : currentTransaction ? (
            <button
              onClick={() => {
                props.paystackCharge?.onSuccess(currentTransaction);
              }}
            >
              Retry Payment
            </button>
          ) : (
            <PaystackButton {...paystackOptions} />
          )}
          {
            <button
              className={`mt-3 cancel-button`}
              onClick={() => props.onCancel()}
            >
              Cancel
            </button>
          }
        </div>
      );
    }

    return (
      <div className="stripe-card-form">
        <CardElement
          className="card-field"
          options={{
            hidePostalCode: true,
            style: {
              base: {
                iconColor: "#009e89",
                color: "#000",
                fontWeight: "500",
                fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
                fontSize: "16px",
                fontSmoothing: "antialiased",
                "::placeholder": {
                  color: "#c2c2c2",
                },
              },
              invalid: {
                iconColor: "#dc3545",
                color: "#dc3545",
              },
            },
          }}
        />
        <div className="d-flex flex-row justify-content-between">
          <button
            onClick={saveStripeCard}
            disabled={props.loading}
            className={`m-auto p-0 ${props.onCancel ? "mr-3" : ""}`}
          >
            {props.loading ? (
              <div className="spinner-border text-dark m-auto" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            ) : (
              "Save"
            )}
          </button>
          {
            <button
              className={`ml-3 cancel-button`}
              onClick={() => props.onCancel()}
            >
              Cancel
            </button>
          }
        </div>
      </div>
    );
  };

  const renderMPESAForm = () => {
    return (
      <div className={"paystack-card-form"}>
        <PhoneInput
          country={"ke"}
          className={""}
          value={phone}
          onChange={(phone, country) => {
            setPhone(phone);
          }}
          countryCodeEditable={false}
          onlyCountries={["ke"]}
          placeholder={"Enter your phone number"}
          isValid={(phone, country) => {
            return (
              (phone.startsWith("2547") && phone.length === 12) ||
              (phone.startsWith("25407") && phone.length === 13)
            );
          }}
        />
        <div className="d-flex flex-row justify-content-between">
          <button
            onClick={saveMPESACard}
            disabled={props.loading}
            className={`m-auto p-0 ${props.onCancel ? "mr-3" : ""}`}
          >
            {props.loading ? (
              <div className="spinner-border text-dark m-auto" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            ) : (
              "Save"
            )}
          </button>
          {
            <button
              className={`ml-3 cancel-button`}
              onClick={() => props.onCancel()}
            >
              Cancel
            </button>
          }
        </div>
      </div>
    );
  };

  const renderCardForm = () => {
    return (
      <div className={"card-section last"}>
        {props.errors && <MessageBlock type={"error"} message={props.errors} />}
        {error && <MessageBlock type={"error"} message={error} />}
        <label className={"col-12 p-0"}>
          Payment Method
          <Dropdown className={"type-switch "}>
            <Dropdown.Toggle>
              {formType === CREDIT_CARD ? "Credit Card" : "MPESA"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => setFormType(CREDIT_CARD)}
                disabled={formType === CREDIT_CARD}
              >
                Credit Card
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => setFormType(MPESA)}
                disabled={formType === MPESA}
              >
                MPESA
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </label>
        {ENABLE_PAYSTACK && (
          <div className={"billing-address mb-3"}>
            {formType !== MPESA && (
              <AddressSelectItem
                formType={BILLING}
                title={"Billing Address"}
                address={props.billingAddress}
                toggleForm={() => setShowAddrForm(!showAddrForm)}
                toggleList={() => setShowAddrList(!showAddrList)}
                clearAddress={props.clearBillingAddress}
              />
            )}
          </div>
        )}
        <div>
          {formType === CREDIT_CARD
            ? renderCreditCardForm()
            : renderMPESAForm()}
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderCardForm()}
      {ENABLE_PAYSTACK && showAddrForm && (
        <AddressForm
          show={showAddrForm}
          toggleForm={() => {
            setShowAddrForm(false);
          }}
          type={BILLING}
          onSuccess={() => {
            setShowAddrForm(false);
          }}
        />
      )}
      {ENABLE_PAYSTACK && showAddrList && (
        <AddressListModal
          toggleList={() => {
            setShowAddrList(!showAddrList);
            if (showAddrList) {
              props.searchAddresses("");
            }
          }}
          showList={showAddrList}
          listType={BILLING}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({
  userDetails,
  applicationContext,
  billingAddress,
}) => ({
  cardList: userDetails.cards,
  info: userDetails.info,
  loading: userDetails.loading,
  errors: userDetails.errors,
  country: applicationContext.context.country,
  billingAddress,
  defaultAddress: userDetails.info?.default_address,
});

export default connect(mapStateToProps, {
  getUserDetails,
  addUserCard,
  clearBillingAddress,
  selectAddress,
  searchAddresses,
})(CardForm);
